import React from 'react';
import { Controller, Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';


// Props do componente
interface PerformanceFieldsetProps {
    context: CollaboratorFormContext;
    control: Control<any>; // Control do React Hook Form do formulário pai
    setValue: UseFormSetValue<any>; // SetValue do React Hook Form do formulário pai
    watch: UseFormWatch<any>; // Watch do React Hook Form do formulário pai
    errors?: Record<string, any>; // Erros do formulário pai
    isSubmitting?: boolean; // Estado de submissão via props
    layout?: 'full' | 'compact'; // Layout via props
}

const PerformanceFieldset: React.FC<PerformanceFieldsetProps> = ({
    context,
    control,
    watch,
    errors = {},
    isSubmitting = false,
}) => {
    // Helper local para layout baseado no contexto
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        const isCompact = context === 'PROFILE_UPDATE';
        return {
            fieldsetStyle: isCompact
                ? "border border-gray-200 p-3 rounded-md"
                : "border border-gray-200 p-4 rounded-md",
            legendStyle: isCompact
                ? "text-sm font-medium px-1 text-gray-600"
                : "text-sm font-medium px-1 text-gray-400"
        };
    };

    // Helper local para extrair mensagem de erro simples
    const getErrorMessage = (fieldName: string): string | null => {
        const error = errors?.[fieldName];
        if (error && typeof error === 'object' && 'message' in error) {
            return error.message as string;
        }
        return null;
    };

    // Watch para campos condicionais
    const rating = watch('rating') || 5;

    // Layout contextual usando helper local
    const layoutConfig = getLayoutByContext(context);

    // Helper para cores do rating
    const getRatingSolidColor = (rating: number): string => {
        if (rating >= 8) return '#22c55e'; // green-500
        if (rating >= 5) return '#f59e0b'; // yellow-500
        if (rating > 0) return '#ef4444'; // red-500
        return '#d1d5db'; // gray-300
    };

    // Verificar se o fieldset deve ser exibido usando schema unificado
    if (!collaboratorSchemaHelpers.shouldShowField('rating', context)) {
        return null;
    }

    return (
        <fieldset className={layoutConfig.fieldsetStyle}>
            <legend className={layoutConfig.legendStyle}>Desempenho ({rating})</legend>
            <div className="mt-1 pt-4 pb-6 flex items-center w-full">
                <Controller
                    name="rating"
                    control={control}
                    render={({ field }) => (
                        <Slider
                            min={1}
                            max={10}
                            value={field.value || 5}
                            onChange={(value) => {
                                const newRating = Array.isArray(value) ? value[0] : value;
                                field.onChange(newRating);
                            }}
                            marks={{ 1: '1', 5: '5', 10: '10' }}
                            className="flex-grow"
                            railStyle={{
                                background: 'linear-gradient(to right, #ef4444, #f59e0b, #22c55e)',
                                height: '12px',
                                borderRadius: '6px',
                            }}
                            trackStyle={{
                                backgroundColor: 'transparent',
                                height: '12px',
                                borderRadius: '6px',
                            }}
                            handleStyle={{
                                borderColor: getRatingSolidColor(field.value || 5),
                                borderWidth: '3px',
                                height: 20,
                                width: 20,
                                marginTop: -4,
                                backgroundColor: 'white',
                                opacity: 1,
                            }}
                            disabled={isSubmitting}
                        />
                    )}
                />
            </div>
            {getErrorMessage('rating') && (
                <p className="mt-2 text-sm text-red-600" data-testid="error-rating">
                    {getErrorMessage('rating')}
                </p>
            )}
        </fieldset>
    );
};

export default PerformanceFieldset; 